import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send,
  MessageSquare,
  Calendar,
  CheckCircle2,
  Zap,
  Users,
  CreditCard
} from 'lucide-react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useSearchParams } from 'react-router-dom';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Card, CardContent } from '@/components/ui/card';

const contactInfo = [
  {
    icon: Phone,
    label: 'Phone',
    value: '07852 872 088',
    href: 'tel:+447852872088',
  },
  {
    icon: Mail,
    label: 'Email',
    value: 'smartsitesolutions@pm.me',
    href: 'mailto:smartsitesolutions@pm.me',
  },
  {
    icon: MapPin,
    label: 'Location',
    value: 'North Cornelly, South Wales, UK',
    href: null,
  },
  {
    icon: Clock,
    label: 'Hours',
    value: 'Mon-Fri: 9am - 6pm',
    href: null,
  },
];

const trustSignals = [
  { text: '0.5s Load Times (React Tech)', icon: Zap },
  { text: 'Local Face-to-Face Consultations', icon: Users },
  { text: 'No Monthly Builder Fees', icon: CreditCard },
];

// SEO Meta and Schema
const pageTitle = "Contact Us | SmartSite Solutions - North Cornelly";
const pageDescription = "Get in touch for trade websites and local SEO in South Wales. Phone: 07852 872 088. Based in North Cornelly. Book a free 15-min strategy call today.";

export default function Contact() {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pre-fill message based on URL params
  useEffect(() => {
    const plan = searchParams.get('plan');
    const addon = searchParams.get('addon');
    
    if (plan) {
      const planNames: Record<string, string> = {
        essentials: 'Essentials (£695)',
        growth: 'Growth Engine (£1,195)',
        leader: 'Market Leader (£2,495)',
      };
      setFormData(prev => ({
        ...prev,
        message: `Hi, I'm interested in the ${planNames[plan] || plan} package. Please get in touch to discuss my project.`,
      }));
    } else if (addon === 'care') {
      setFormData(prev => ({
        ...prev,
        message: `Hi, I'm interested in the SmartSite Care Plan (£39/month). Please get in touch to discuss.`,
      }));
    }
  }, [searchParams]);

  // Set page title and meta
  useEffect(() => {
    // Add ContactPage schema
    const schema = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact SmartSite Solutions",
      "description": pageDescription,
      "url": "https://smartsite-lead-gen.lovable.app/contact",
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "SmartSite Solutions",
        "telephone": "+447852872088",
        "email": "smartsitesolutions@pm.me",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "North Cornelly",
          "addressRegion": "South Wales",
          "addressCountry": "UK"
        },
        "areaServed": {
          "@type": "GeoCircle",
          "geoMidpoint": {
            "@type": "GeoCoordinates",
            "latitude": 51.6604,
            "longitude": -3.8032
          },
          "geoRadius": "50000"
        }
      }
    };

    let scriptTag = document.querySelector('script[data-schema="contact"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-schema', 'contact');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(schema);

    return () => {
      if (scriptTag) {
        scriptTag.remove();
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          access_key: 'ad32044d-23dc-46ab-b3b3-89aa110a09c3',
          subject: `New enquiry from ${formData.name} - SmartSite Solutions`,
          from_name: formData.name,
          email: formData.email,
          phone: formData.phone || 'Not provided',
          business: formData.business || 'Not provided',
          message: formData.message,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Message sent!",
          description: "We'll get back to you within 24 hours.",
        });
        setFormData({ name: '', email: '', phone: '', business: '', message: '' });
      } else {
        throw new Error(result.message || 'Something went wrong');
      }
    } catch (error) {
      toast({
        title: "Error sending message",
        description: "Please try again or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Contact', href: '/contact' }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href="https://smartsite-lead-gen.lovable.app/contact" />
      </Helmet>
      <Navbar />
      
      <main className="pt-32 pb-24">
        <div className="container mx-auto px-4 lg:px-8">
          {/* Breadcrumbs */}
          <Breadcrumbs items={breadcrumbItems} />

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-slate-400 mb-6">
              Get in Touch
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Start Your{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Digital Engineering
              </span>{' '}
              Project
            </h1>
            <p className="text-lg text-slate-400">
              Based in North Cornelly. Serving Trades across South Wales & The M4 Corridor.
            </p>
          </motion.div>

          {/* Split Screen Layout */}
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 max-w-6xl mx-auto">
            
            {/* Left Side - Trust Signals & Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="space-y-8"
            >
              {/* Why Us Section */}
              <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl overflow-hidden">
                <CardContent className="p-6 md:p-8">
                  <h2 className="font-display text-2xl font-bold text-white mb-6">
                    Why{' '}
                    <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                      SmartSite?
                    </span>
                  </h2>
                  <ul className="space-y-4">
                    {trustSignals.map((signal, index) => (
                      <motion.li
                        key={signal.text}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 + index * 0.1 }}
                        className="flex items-center gap-4"
                      >
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-orange-500/20 to-orange-600/10 flex items-center justify-center flex-shrink-0">
                          <CheckCircle2 className="w-5 h-5 text-orange-400" />
                        </div>
                        <span className="text-slate-300 font-medium">{signal.text}</span>
                      </motion.li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Contact Details */}
              <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl overflow-hidden">
                <CardContent className="p-6 md:p-8">
                  <h3 className="font-display font-bold text-lg text-white mb-6">Contact Details</h3>
                  <div className="space-y-4">
                    {contactInfo.map((item) => (
                      <div key={item.label}>
                        {item.href ? (
                          <a 
                            href={item.href}
                            className="flex items-center gap-4 p-3 rounded-xl hover:bg-white/5 transition-colors -mx-3"
                          >
                            <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                              <item.icon className="w-5 h-5 text-cyan-400" />
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">{item.label}</p>
                              <p className="font-medium text-white">{item.value}</p>
                            </div>
                          </a>
                        ) : (
                          <div className="flex items-center gap-4 p-3 -mx-3">
                            <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                              <item.icon className="w-5 h-5 text-cyan-400" />
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">{item.label}</p>
                              <p className="font-medium text-white">{item.value}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* WhatsApp CTA */}
              <Card className="bg-white/5 backdrop-blur-xl border-white/10 rounded-2xl overflow-hidden">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white">Prefer WhatsApp?</p>
                    <p className="text-sm text-slate-400">Send us a message directly</p>
                  </div>
                  <a
                    href="https://wa.me/447852872088?text=Hi%2C%20I'd%20like%20to%20discuss%20a%20website%20project"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="ghost" className="bg-white/5 border border-white/10 text-white hover:bg-white/10">
                      Chat Now
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </motion.div>

            {/* Right Side - Contact Form */}
            <motion.div
              id="contact-form"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-white/5 backdrop-blur-xl border-slate-700 rounded-2xl overflow-hidden shadow-2xl">
                <CardContent className="p-6 md:p-8 lg:p-10">
                  <h2 className="font-display text-2xl font-bold text-white mb-6">Send a Message</h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot for spam protection */}
                    <input type="checkbox" name="botcheck" className="hidden" style={{ display: 'none' }} />
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Your Name</label>
                        <Input
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="John Smith"
                          className="bg-white/5 border-white/10 focus:border-orange-500/50 text-white placeholder:text-slate-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                        <Input
                          required
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="john@example.com"
                          className="bg-white/5 border-white/10 focus:border-orange-500/50 text-white placeholder:text-slate-500"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Phone (Optional)</label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="07123 456 789"
                          className="bg-white/5 border-white/10 focus:border-orange-500/50 text-white placeholder:text-slate-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Business Name</label>
                        <Input
                          value={formData.business}
                          onChange={(e) => setFormData({ ...formData, business: e.target.value })}
                          placeholder="Smith Plumbing"
                          className="bg-white/5 border-white/10 focus:border-orange-500/50 text-white placeholder:text-slate-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">How can we help?</label>
                      <Textarea
                        required
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Tell us about your project and goals..."
                        rows={5}
                        className="bg-white/5 border-white/10 focus:border-orange-500/50 text-white placeholder:text-slate-500 resize-none"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white border-0 py-6 text-base font-semibold rounded-xl shadow-lg shadow-orange-500/25"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2 w-4 h-4" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Full-Width Strategy Call Banner */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="max-w-6xl mx-auto mt-16"
          >
            <Card className="bg-gradient-to-br from-cyan-500/10 via-blue-500/10 to-cyan-600/5 backdrop-blur-xl border-cyan-500/20 rounded-2xl overflow-hidden">
              <CardContent className="p-6 md:p-8 lg:p-12">
                <div className="flex flex-col lg:flex-row items-center gap-6 md:gap-8">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-cyan-500/20 to-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-8 h-8 text-cyan-400" />
                  </div>
                  <div className="flex-1 text-center lg:text-left">
                    <h3 className="font-display font-bold text-2xl text-white mb-2">
                      Not Ready to Quote?
                    </h3>
                    <p className="text-slate-400 text-lg">
                      Book a free 15-minute roadmap call. No pressure, just clarity on your next steps.
                    </p>
                  </div>
                  <a
                    href="https://calendly.com/smartsitesolutions-pm/30min"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0"
                  >
                    <Button className="primary-cta h-14 px-8 text-base">
                      Book Free Strategy Call
                      <Calendar className="ml-2 w-5 h-5" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
